import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useUI } from '@/stores/ui';
import { Icon } from './icon';
import { cn } from '@/lib/cn';
import type { Block } from '@focus/shared';

const WEEKDAY_SHORT = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];

export function MonthView() {
  const { monthOffset, goPrevMonth, goNextMonth, goThisMonth, setView, setDayOffset } = useUI();

  // Compute the month's first Monday-aligned cell and last cell
  const { cells, monthLabel } = useMemo(() => {
    const base = new Date();
    base.setHours(0, 0, 0, 0);
    const cur = new Date(base.getFullYear(), base.getMonth() + monthOffset, 1);
    const first = new Date(cur);
    const dow = (first.getDay() + 6) % 7;
    first.setDate(first.getDate() - dow);
    const cells: Date[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(first);
      d.setDate(first.getDate() + i);
      cells.push(d);
    }
    const label = cur.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
    return { cells, monthLabel: label };
  }, [monthOffset]);

  // Load blocks for each week we touch. We need up to 6 weeks of blocks.
  const firstMonday = cells[0].getTime();
  const weekStarts = useMemo(() => {
    const starts: number[] = [];
    for (let w = 0; w < 6; w++) starts.push(firstMonday + w * 7 * 86400000);
    return starts;
  }, [firstMonday]);

  const weekQueries = useQuery({
    queryKey: ['blocks-month', firstMonday],
    queryFn: async () => {
      const all: Block[] = [];
      for (const ws of weekStarts) {
        try {
          const r = await api.listBlocks(ws);
          all.push(...r);
        } catch { /* ignore */ }
      }
      return all;
    },
  });
  const blocks = weekQueries.data || [];

  const blocksByDay = useMemo(() => {
    const m = new Map<string, Block[]>();
    for (const b of blocks) {
      const d = new Date(b.weekStart + b.dayIdx * 86400000);
      d.setHours(0, 0, 0, 0);
      const k = d.toISOString().slice(0, 10);
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(b);
    }
    return m;
  }, [blocks]);

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const curMonthIdx = new Date().getMonth() + monthOffset;

  const openDay = (d: Date) => {
    const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
    setDayOffset(diff);
    setView('day');
  };

  return (
    <div className="flex flex-col h-full bg-paper dark:bg-dark-paper">
      <div className="flex items-center gap-2 md:gap-3 px-2 md:px-4 h-[44px] md:h-[54px] border-b border-line dark:border-dark-line">
        <div className="text-[15px] md:text-[18px] font-semibold tracking-[-0.3px] font-display text-fg dark:text-dark-fg truncate">
          {monthLabel}
        </div>
        <div className="ml-auto flex gap-1">
          <button onClick={goPrevMonth} className="btn-ghost h-8 px-2" title="Vorheriger Monat"><Icon name="chevronLeft" size={14} /></button>
          <button onClick={goThisMonth} className="btn-ghost h-8 px-2 text-[12px] hidden md:inline-flex" title="Dieser Monat">Dieser Monat</button>
          <button onClick={goThisMonth} className="btn-ghost h-8 px-2 text-[12px] md:hidden" title="Dieser Monat">Heute</button>
          <button onClick={goNextMonth} className="btn-ghost h-8 px-2" title="Nächster Monat"><Icon name="chevronRight" size={14} /></button>
        </div>
      </div>

      <div className="grid grid-cols-7 border-b border-line dark:border-dark-line">
        {WEEKDAY_SHORT.map((d, i) => (
          <div key={d} className={cn(
            'px-3 py-2 text-[12px] font-semibold tracking-[-0.1px] text-fg dark:text-dark-fg',
            i > 0 && 'border-l border-line dark:border-dark-line',
          )}>{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7 flex-1 overflow-hidden">
        {cells.map((d) => {
          const isToday = d.getTime() === today.getTime();
          const inMonth = d.getMonth() === (((curMonthIdx % 12) + 12) % 12);
          const k = d.toISOString().slice(0, 10);
          const dayBlocks = blocksByDay.get(k) || [];
          const deepCount = dayBlocks.filter((b) => b.cat === 'blockDeep').length;
          const mtgCount = dayBlocks.filter((b) => b.cat === 'blockMeeting').length;
          const otherCount = dayBlocks.length - deepCount - mtgCount;
          return (
            <button
              key={k}
              onClick={() => openDay(d)}
              className={cn(
                'relative border-t border-l border-line dark:border-dark-line p-1.5 text-left flex flex-col gap-1 hover:bg-paperAlt dark:hover:bg-dark-paperAlt transition min-h-0',
                isToday && 'bg-accentSoft dark:bg-accent/15',
                !inMonth && 'opacity-40',
              )}
            >
              <div className="flex items-baseline gap-1.5">
                <span className={cn('text-[13px] font-semibold', isToday ? 'text-accent' : 'text-fg dark:text-dark-fg')}>
                  {d.getDate()}.
                </span>
                {dayBlocks.length > 0 && (
                  <span className="text-[10px] font-mono text-fg dark:text-dark-fg">
                    {dayBlocks.length}
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-0.5">
                {Array.from({ length: deepCount }).map((_, i) => (
                  <span key={`d${i}`} className="w-1.5 h-1.5 rounded-full" style={{ background: 'oklch(0.55 0.17 255)' }} />
                ))}
                {Array.from({ length: mtgCount }).map((_, i) => (
                  <span key={`m${i}`} className="w-1.5 h-1.5 rounded-full" style={{ background: 'oklch(0.55 0.15 300)' }} />
                ))}
                {Array.from({ length: otherCount }).map((_, i) => (
                  <span key={`o${i}`} className="w-1.5 h-1.5 rounded-full bg-muted dark:bg-dark-muted" />
                ))}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
